/**
 * Geometría de la vista isométrica 2.5D de la carga. Todo en milímetros del contenedor:
 * x = largo (de la cabina hacia las puertas), y = ancho, z = altura.
 */
export interface IsoDims {
  length: number;
  width: number;
  height: number;
}

export interface IsoBlock {
  key: string;
  productId: number | null;
  label: string;
  x: number;
  y: number;
  z: number;
  l: number;
  w: number;
  h: number;
  level: number;
  stackSize: number;
  fragile: boolean;
  color: string;
  /** Pallet físico al que pertenece el bloque; los tramos de un pallet mixto comparten el mismo. */
  palletId?: string;
  /** Fracción de un pallet que representa el bloque (solo en pallets parciales del reparto ilustrativo). */
  partialFill?: number;
  /** Tramo que va encima de otro en el mismo pallet: no dibuja base de madera propia. */
  sharedPallet?: boolean;
  /** Pisos (capas de cajas) del pallet completo y cajas por piso, cuando el maestro logístico los trae. */
  layers?: number;
  boxesPerLayer?: number;
}

export interface IsoPlacementInput {
  product_id: number;
  product_name: string;
  sku: string;
  x_mm: number;
  y_mm: number;
  z_mm: number;
  oriented_length_mm: number;
  oriented_width_mm: number;
  height_mm: number;
  level: number;
  stack_id: string;
  fragile: boolean;
}

export interface IsoPalletDemand {
  productId: number | null;
  label: string;
  color: string;
  /** Pallets equivalentes con decimales (0,17 = una sexta parte de un pallet). */
  palletEquivalent: number;
  layers?: number | null;
  boxesPerLayer?: number | null;
  stackable: boolean | null;
  fragile: boolean;
  maxStackPallets: number | null;
}

/** Medidas de un tráiler estándar cuando el contenedor no trae dimensiones internas. */
export const DEFAULT_ISO_DIMS: IsoDims = { length: 13600, width: 2450, height: 2700 };

const PALLET_LENGTH_MM = 1200;
const PALLET_WIDTH_MM = 800;
const PALLET_HEIGHT_MM = 1100;
export const PALLET_BASE_MM = 140;
const MAX_SYNTHETIC_PALLETS = 400;

// Ejes en pantalla: el largo corre a la derecha, el ancho hacia delante-izquierda.
const LENGTH_AXIS: [number, number] = [0.96, 0.28];
const WIDTH_AXIS: [number, number] = [-0.55, 0.42];

export type IsoPoint = [number, number];

/** Proyecta un punto del contenedor (mm) a coordenadas de pantalla (mm, y hacia abajo). */
export function projectIso(x: number, y: number, z: number): IsoPoint {
  return [
    x * LENGTH_AXIS[0] + y * WIDTH_AXIS[0],
    x * LENGTH_AXIS[1] + y * WIDTH_AXIS[1] - z,
  ];
}

export function blocksFromPlacements(
  placements: IsoPlacementInput[],
  colorFor: (productId: number) => string,
): IsoBlock[] {
  const stackSizes = new Map<string, number>();
  placements.forEach((placement) => {
    stackSizes.set(placement.stack_id, (stackSizes.get(placement.stack_id) || 0) + 1);
  });
  return placements.map((placement) => ({
    key: `${placement.stack_id}-${placement.level}`,
    productId: placement.product_id,
    label: placement.product_name || placement.sku,
    x: placement.x_mm,
    y: placement.y_mm,
    z: placement.z_mm,
    l: placement.oriented_length_mm,
    w: placement.oriented_width_mm,
    h: placement.height_mm,
    level: placement.level,
    stackSize: stackSizes.get(placement.stack_id) || 1,
    fragile: placement.fragile,
    color: colorFor(placement.product_id),
  }));
}

/**
 * Reparte un total de pallets enteros entre productos según sus pallets equivalentes
 * (método del mayor resto). El total es el equivalente global redondeado hacia arriba,
 * así que un producto menor que un pallet puede quedarse sin ninguno.
 */
export function allocatePalletCounts(equivalents: number[]): number[] {
  const safe = equivalents.map((value) => (Number.isFinite(value) && value > 0 ? value : 0));
  const total = Math.ceil(safe.reduce((sum, value) => sum + value, 0) - 1e-9);
  const counts = safe.map((value) => Math.floor(value + 1e-9));
  let remaining = total - counts.reduce((sum, value) => sum + value, 0);
  const byRemainder = safe
    .map((value, index) => ({ index, remainder: value - Math.floor(value + 1e-9) }))
    .sort((a, b) => b.remainder - a.remainder);
  for (let i = 0; remaining > 0 && i < byRemainder.length; i += 1, remaining -= 1) {
    counts[byRemainder[i].index] += 1;
  }
  return counts;
}

export interface PalletShare {
  demand: IsoPalletDemand;
  /** Parte de un pallet que ocupa el producto (0-1). */
  fraction: number;
}

export interface PalletPlan {
  /** Pallets completos por producto. */
  whole: Array<{ demand: IsoPalletDemand; count: number }>;
  /** Pallets mixtos: cada uno agrupa fracciones de productos que suman como máximo un pallet. */
  mixed: PalletShare[][];
}

/**
 * Reparte los pallets equivalentes (con decimales) en pallets físicos: cada producto tiene tantos
 * pallets completos como pallets enteros equivalentes; las fracciones sobrantes, de mayor a menor,
 * se juntan en pallets mixtos. El total es el equivalente global redondeado hacia arriba.
 */
export function planPallets(demands: IsoPalletDemand[]): PalletPlan {
  const whole: PalletPlan['whole'] = [];
  const partials: PalletShare[] = [];
  demands.forEach((demand) => {
    const equivalent = Number.isFinite(demand.palletEquivalent) && demand.palletEquivalent > 0 ? demand.palletEquivalent : 0;
    const count = Math.floor(equivalent + 1e-9);
    const fraction = equivalent - count;
    if (count > 0) whole.push({ demand, count });
    if (fraction > 1e-9) partials.push({ demand, fraction });
  });
  partials.sort((a, b) => b.fraction - a.fraction);
  const mixed: PalletShare[][] = [];
  let free = 0;
  partials.forEach(({ demand, fraction }) => {
    let remaining = fraction;
    while (remaining > 1e-9) {
      if (free <= 1e-9) {
        mixed.push([]);
        free = 1;
      }
      const take = Math.min(remaining, free);
      mixed[mixed.length - 1].push({ demand, fraction: take });
      free -= take;
      remaining -= take;
    }
  });
  return { whole, mixed };
}

/**
 * Reparto ilustrativo cuando todavía no hay plan de estiba: pallets estándar 1200 × 800
 * en filas a lo largo del contenedor, apilando los pallets completos de los productos que lo permiten.
 * En un pallet mixto cada producto es un tramo cuya altura es lo que ocupa del pallet.
 */
export function synthesizeBlocks(
  demands: IsoPalletDemand[],
  dims: IsoDims,
): { blocks: IsoBlock[]; overflow: number; partialProducts: number } {
  const across = Math.max(1, Math.floor(dims.width / PALLET_WIDTH_MM));
  const rows = Math.max(1, Math.floor(dims.length / PALLET_LENGTH_MM));
  const slots = across * rows;
  const yOffset = Math.max(0, (dims.width - across * PALLET_WIDTH_MM) / 2);
  const palletHeight = Math.min(PALLET_HEIGHT_MM, dims.height);
  const cargoCapacity = Math.max(1, palletHeight - PALLET_BASE_MM);
  const blocks: IsoBlock[] = [];
  const partialProductIds = new Set<string>();
  const plan = planPallets(demands);
  let slot = 0;
  let overflow = 0;
  let budget = MAX_SYNTHETIC_PALLETS;
  const position = (target: number) => ({
    x: Math.floor(target / across) * PALLET_LENGTH_MM,
    y: yOffset + (target % across) * PALLET_WIDTH_MM,
  });

  plan.whole.forEach(({ demand, count }) => {
    const canStack = demand.stackable !== false && !demand.fragile;
    const maxLevels = canStack
      ? Math.max(1, Math.min(demand.maxStackPallets && demand.maxStackPallets > 0 ? demand.maxStackPallets : 2, Math.floor(dims.height / palletHeight)))
      : 1;
    let remaining = count;
    let stackNumber = 0;
    while (remaining > 0) {
      if (slot >= slots || budget <= 0) {
        overflow += remaining;
        break;
      }
      const levels = Math.min(maxLevels, remaining);
      const { x, y } = position(slot);
      for (let level = 1; level <= levels; level += 1) {
        const key = `${demand.productId ?? demand.label}-${stackNumber}-${level}`;
        blocks.push({
          key,
          palletId: key,
          productId: demand.productId,
          label: demand.label,
          x,
          y,
          z: (level - 1) * palletHeight,
          l: PALLET_LENGTH_MM,
          w: PALLET_WIDTH_MM,
          h: palletHeight,
          level,
          stackSize: levels,
          fragile: demand.fragile,
          color: demand.color,
          ...(demand.layers && demand.layers > 0 ? { layers: Math.round(demand.layers) } : {}),
          ...(demand.boxesPerLayer && demand.boxesPerLayer > 0 ? { boxesPerLayer: Math.round(demand.boxesPerLayer) } : {}),
        });
      }
      slot += 1;
      stackNumber += 1;
      remaining -= levels;
      budget -= levels;
    }
  });

  plan.mixed.forEach((shares, mixedIndex) => {
    if (slot >= slots || budget <= 0) {
      overflow += 1;
      return;
    }
    const { x, y } = position(slot);
    let cargoUsed = 0;
    shares.forEach(({ demand, fraction }, segment) => {
      const cargoHeight = Math.max(30, cargoCapacity * fraction);
      const first = segment === 0;
      blocks.push({
        key: `mixed-${mixedIndex}-${segment}-${demand.productId ?? demand.label}`,
        palletId: `mixed-${mixedIndex}`,
        productId: demand.productId,
        label: demand.label,
        x,
        y,
        z: first ? 0 : PALLET_BASE_MM + cargoUsed,
        l: PALLET_LENGTH_MM,
        w: PALLET_WIDTH_MM,
        h: first ? PALLET_BASE_MM + cargoHeight : cargoHeight,
        level: 1,
        stackSize: 1,
        fragile: demand.fragile,
        color: demand.color,
        partialFill: fraction,
        sharedPallet: !first,
      });
      partialProductIds.add(String(demand.productId ?? demand.label));
      cargoUsed += cargoHeight;
    });
    slot += 1;
    budget -= 1;
  });
  return { blocks, overflow, partialProducts: partialProductIds.size };
}

/** Orden de carga (y de animación): del fondo hacia las puertas, planta a planta. */
export function sortForLoading(blocks: IsoBlock[]): IsoBlock[] {
  return [...blocks].sort((a, b) => a.x - b.x || a.y - b.y || a.z - b.z);
}

/** Orden de pintado (painter's algorithm): lo más lejano primero. */
export function sortForPainting(blocks: IsoBlock[]): IsoBlock[] {
  const depth = (block: IsoBlock) => block.x + block.l / 2 + block.y + block.w / 2 + (block.z + block.h / 2) * 1.2;
  return [...blocks].sort((a, b) => depth(a) - depth(b));
}

/** Silueta convexa de un conjunto de puntos (cadena monótona de Andrew). */
export function convexHull(points: IsoPoint[]): IsoPoint[] {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  if (sorted.length < 3) return sorted;
  const cross = (o: IsoPoint, a: IsoPoint, b: IsoPoint) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower: IsoPoint[] = [];
  sorted.forEach((point) => {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], point) <= 0) lower.pop();
    lower.push(point);
  });
  const upper: IsoPoint[] = [];
  [...sorted].reverse().forEach((point) => {
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], point) <= 0) upper.pop();
    upper.push(point);
  });
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

export function blockCorners(block: Pick<IsoBlock, 'x' | 'y' | 'z' | 'l' | 'w' | 'h'>): IsoPoint[] {
  const corners: IsoPoint[] = [];
  [0, 1].forEach((i) => [0, 1].forEach((j) => [0, 1].forEach((k) => {
    corners.push(projectIso(block.x + i * block.l, block.y + j * block.w, block.z + k * block.h));
  })));
  return corners;
}
